import { parse, stringify, ParseOptions } from "query-string";

import React, { useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";

export interface Options {
  navigateMode?: "push" | "replace";
}

const parseConfig = {
  skipEmptyString: true,
  parseNumbers: true,
  parseBooleans: true,
  arrayFormat: "comma",
} as ParseOptions;
interface UrlState {
  [key: string]: any;
}

export const useURLStateV2 = <S extends UrlState = UrlState>(
  initialState?: S | (() => S),
  options?: Partial<Options & ParseOptions>
) => {
  type State = Partial<UrlState>;
  const { navigateMode = "push", ...parseOptions } = options || {};
  const location = useLocation();
  const history = useHistory();

  const update = useState(false)[1];

  const initialStateRef = useRef(typeof initialState === "function" ? (initialState as () => S)() : initialState || {})
    .current as State;

  const queryFromUrl = useMemo(() => {
    return parse(location.search, { ...parseConfig, ...parseOptions });
  }, [location.search, parseOptions]);

  const targetQuery: State = useMemo(
    () => ({
      ...initialStateRef,
      ...Object.entries(queryFromUrl).reduce(
        (acc, [name, value]) => ({
          ...acc,
          [name]: Array.isArray(initialStateRef[name as string]) ? [value].flat() : value,
        }),
        {}
      ),
    }),
    /* eslint-disable react-hooks/exhaustive-deps */
    [queryFromUrl]
  );

  const setState = (state: React.SetStateAction<State>) => {
    const newQuery = typeof state === "function" ? state(targetQuery) : state;
    update((v) => !v);
    history[navigateMode]({
      hash: location.hash,
      search: stringify({ ...queryFromUrl, ...newQuery }, { ...parseConfig, ...parseOptions }) || "?",
    });
  };

  return [targetQuery, setState] as const;
};

export default useURLStateV2;
